import React, { useState, useEffect } from 'react';
import { Card, Button, Collapse, Form, Spinner } from 'react-bootstrap';
import SubtleBadge from 'components/common/SubtleBadge';
import classNames from 'classnames';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'react-datepicker';
import { IoFilter } from 'react-icons/io5';
import { MdOutlineFilterListOff } from 'react-icons/md';
import { useSelector, useDispatch } from 'react-redux';
import { getRecetteBoutique } from 'data/Finance/caisse';

function DatepickerFiltre({ startDate, endDate, onChange }) {
  return (
    <DatePicker
      popperPlacement="bottom-start"
      selected={startDate}
      onChange={onChange}
      startDate={startDate}
      endDate={endDate}
      formatWeekDay={day => day.slice(0, 3)}
      selectsRange
      dateFormat="dd/MM/YYYY"
      className="form-control"
      onKeyDown={e => e.preventDefault()}
    />
  );
}

const columns = [
  {
    accessorKey: 'ID',
    header: 'id',
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'magasin',
    header: 'Magasin',
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'etatFinance',
    header: 'Etat traitement',
    meta: {
      headerProps: { className: 'text-start text-900 w-2' },
      cellProps: { className: 'ps-2 text-start w-5 fs-10' },
    },
    cell: ({ row: { original } }) => {
      const { etatFinance } = original;
      return (
        <SubtleBadge
          pill
          bg={
            etatFinance === 'En attente de traitement' || !etatFinance
              ? 'warning'
              : etatFinance === 'Validé'
              ? 'success'
              : etatFinance === 'A Réctifier'
              ? 'danger'
              : etatFinance === 'Enveloppe validé'
              ? 'primary'
              : 'secondary'
          }
          className="d-flex flex-center"
        >
          <p className="mb-0">{etatFinance || 'En attente de traitement'}</p>
        </SubtleBadge>
      );
    },
  },
  {
    accessorKey: 'date',
    header: 'Date début',
    accessorFn: row => {
      return (
        row.DateDebut &&
        new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(row.DateDebut))
      );
    },
    meta: {
      headerProps: { className: 'white-space-nowrap text-900 px-1' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10 px-1' },
    },
  },
  {
    accessorKey: 'date',
    header: 'Date fin',
    accessorFn: row => {
      return (
        row.DateDebut &&
        new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(row.DateDebut))
      );
    },
    meta: {
      headerProps: { className: 'white-space-nowrap text-900 px-1' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10 px-1' },
    },
  },
  {
    accessorKey: 'montant',
    header: 'Montant',
    accessorFn: row => row.montant?.toFixed(3),
    meta: {
      headerProps: { className: 'white-space-nowrap text-900' },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'ouverture',
    header: 'Ouverture',
    meta: {
      headerProps: {
        className: 'text-center text-900',
      },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'fermeture',
    header: 'Fermeture',
    meta: {
      headerProps: {
        className: 'text-center text-900',
      },
      cellProps: { className: 'font-sans-serif white-space-nowrap fs-10' },
    },
  },
  {
    accessorKey: 'etatSession',
    header: 'Etat',
    meta: {
      headerProps: { className: 'text-end text-900 w-2' },
      cellProps: { className: 'ps-2 text-end w-5 fs-10' },
    },
  },
  {
    accessorKey: 'dateTraitement',
    header: 'Date traitement',
    meta: {
      headerProps: { className: 'text-end text-900 w-2' },
      cellProps: { className: 'ps-2 text-end w-5 fs-10' },
    },
    accessorFn: row => {
      return row.dateTraitement
        ? new Intl.DateTimeFormat('fr-FR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }).format(new Date(row.dateTraitement) - 60 * 60 * 1000)
        : '';
    },
  },
];

const EntetesFactures = ({
  selectedRecettes,
  setSelectedRecettes,
  setShowDetails,
  fetchRecettes,
  currentCondition,
  setCurrentCondition,
  factures,
  setFactures,
  loading,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [lastSelected, setLastSelected] = useState(null);

  const { user } = useSelector(state => state.auth);

  const onDateChange = dates => {
    const [start, end] = dates;
    setCurrentCondition({
      ...currentCondition,
      date: [start, end],
    });
  };

  const table = useAdvanceTable({
    data: factures,
    columns: columns,
    selection: true,
    sortable: true,
    pagination: true,
    perPage: 5,
    selectionColumnWidth: 52,
    getRowId: row => `${row.ID}_${row.date || ''}`,
  });

  const handleSubmit = e => {
    e.preventDefault();
    fetchRecettes();
    setShowFilters(false);
  };

  const handleReset = async () => {
    const resetCondition = {
      date: [
        new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        new Date(Date.now()).toISOString(),
      ],
      magasin: 'Tous',
      session: '',
      etat: 'Tous',
    };
    setCurrentCondition(resetCondition);
    // setCondition(resetCondition);
    // setSelectedFactures([]);
    setShowFilters(false);
  };

  useEffect(() => {
    // Create a Set of selected IDs for faster lookups
    const selectedIds = new Set(selectedRecettes.map(recette => recette.ID));

    // Build the new row selection state
    const newRowSelection = {};
    table.getRowModel().rows.forEach(row => {
      if (selectedIds.has(row.original.ID)) {
        newRowSelection[row.id] = true;
      }
    });

    // Only update if the selection actually changed
    const currentSelection = table.getState().rowSelection || {};
    const currentKeys = Object.keys(currentSelection);
    const newKeys = Object.keys(newRowSelection);

    // Check if the selection has changed
    if (
      currentKeys.length !== newKeys.length ||
      !currentKeys.every(key => newRowSelection[key] === currentSelection[key])
    ) {
      table.setRowSelection(newRowSelection);
    }
  }, [selectedRecettes, table]);

  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows.map(row => row.original);
    if (selectedRows.length > 0) {
      if (selectedRows.length === 1) {
        setSelectedRecettes([selectedRows[0]]);
        setLastSelected(selectedRows[0]);
      } else {
        setSelectedRecettes(selectedRows.filter(row => row.ID !== lastSelected.ID));
        setLastSelected(selectedRows.filter(row => row.ID !== lastSelected.ID));
      }
    } else {
      setSelectedRecettes([]);
      setLastSelected(null);
    }
    console.log(selectedRows);
  }, [table.getSelectedRowModel().rows, setSelectedRecettes]);

  return (
    <Card className="h-100" style={{ width: '100%', maxHeight: '450px' }}>
      <Card.Header style={{ borderBottom: '1px solid #e0e0e0' }}>
        <Card.Title className="d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2 align-items-center">
            <h5>Liste des recettes</h5>
            {/* <Button
              variant="outline-primary"
              disabled={
                selectedRecettes.length === 0 ||
                selectedRecettes.length > 1 ||
                selectedRecettes[0].etatFinance !== 'Enveloppe validé'
              }
              onClick={() => setShowDetails(prev => !prev)}
            >
              Afficher Détails
            </Button> */}
          </div>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-primary" onClick={() => setShowFilters(v => !v)}>
              <IoFilter />
            </Button>
            <Button size="sm" variant="outline-danger" onClick={() => handleReset()}>
              <MdOutlineFilterListOff />
            </Button>
          </div>
        </Card.Title>
      </Card.Header>
      <Collapse in={showFilters}>
        <div className="p-3 border-bottom bg-light">
          <Form onSubmit={handleSubmit}>
            <div className="d-flex justify-content-between align-items-center">
              <Form.Group>
                <Form.Label>Date</Form.Label>
                <DatepickerFiltre
                  startDate={currentCondition.date[0]}
                  endDate={currentCondition.date[1]}
                  onChange={onDateChange}
                />
              </Form.Group>
              <Form.Group className="ms-3 w-50">
                <Form.Label>Magasin</Form.Label>
                <Form.Select
                  size="sm"
                  defaultValue={user.magasin}
                  onChange={e =>
                    setCurrentCondition({ ...currentCondition, magasin: e.target.value })
                  }
                  value={user.magasin === 'Tous' ? 'Tous' : user.magasin}
                  //   disabled={user.magasin !== 'Tous'}
                >
                  <option>Tous</option>
                  <option>EZZAHRA</option>
                  <option>BOUMHEL</option>
                  <option>SOUKRA</option>
                  <option>SFAX</option>
                  <option>MARSA</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <Form.Group>
                <Form.Label>Session</Form.Label>
                <Form.Control
                  style={{ width: '100px' }}
                  type="number"
                  value={currentCondition.session}
                  onChange={e =>
                    setCurrentCondition({ ...currentCondition, session: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="ms-3 w-50">
                <Form.Label>Etat</Form.Label>
                <Form.Select
                  size="sm"
                  defaultValue={currentCondition.typeFacture}
                  onChange={e =>
                    setCurrentCondition({ ...currentCondition, typeFacture: e.target.value })
                  }
                  value={currentCondition.typeFacture}
                >
                  <option>Tous</option>
                  <option>En attente de traitement</option>
                  <option>En cours de traitement</option>
                  <option>Validé</option>
                  <option>A Réctifier</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="d-flex justify-content-end">
              <Button variant="primary" type="submit" className="mt-3">
                Rechercher
              </Button>
            </div>
          </Form>
        </div>
      </Collapse>
      <Card.Body style={{ maxHeight: '450px', overflowY: 'auto' }}>
        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 200 }}
          >
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="bg-200 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                bordered: true,
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden',
              }}
            />
            <AdvanceTableFooter rowInfo navButtons />
          </AdvanceTableProvider>
        )}
      </Card.Body>
    </Card>
  );
};

export default EntetesFactures;
